import enMessages from 'devextreme/localization/messages/en.json';
import deMessages from 'devextreme/localization/messages/de.json';
import esMessages from 'devextreme/localization/messages/es.json';
import frMessages from 'devextreme/localization/messages/fr.json';
import itMessages from 'devextreme/localization/messages/it.json';
import nlMessages from 'devextreme/localization/messages/nl.json';
import plMessages from 'devextreme/localization/messages/pl.json';
import { Component, OnInit } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { forkJoin, of, Subject } from 'rxjs';
import { loadMessages, locale } from 'devextreme/localization';
import { DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { DxDrawerTypes } from 'devextreme-angular/ui/drawer';
import { AuthorizationService } from './services/authorization.service';
import { AuthenticatedPersonModel } from './models/person/authenticated-person.model';
import { supportIcon } from './app-icons';
import { PersonModel } from './models/person/person.model';
import { TranslateService } from '@ngx-translate/core';
import { StorageKey, StorageService } from './services/storage.service';
import {distinctUntilChanged,filter, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import { AppInsightsService } from './services/app-insights.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AngularFaviconService } from 'angular-favicon';
import config from 'devextreme/core/config';
import { licenseKey } from '../devextreme-license';
import { OrganisationUnitModel } from './models/organisation-unit/organisation-unit.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PersonService } from './services/person.service';
import { UserContextService } from './services/user-context.service';
import { SegmentTranslationService } from './services/segment-translation.service';
import { OnboardingService } from './services/onboarding/onboarding.service';
import { DrawerService } from './services/drawer.service';
import { fetchSetup } from '@devexpress/analytics-core/analytics-utils';
import { GeneralSettingsService } from './services/general-settings.service';
import { TenantContextService } from './services/tenant-context.service';
import { TenantRedirectService } from './services/tenant-redirect.service';
import { AppRoutes } from './constants/app-routes';
import { TenantTypeModel } from './models/tenants/tenant-type.model';
import { RegistrationLinkService } from './services/registration-link.service';
import { RegistrationFlowService } from './services/registration-flow.service';
import { PendingRegistrationFlowModel } from './models/registration-flow/pending-registration-flow.model';
import { TenantVideoService } from './services/tenants/tenant-video.service';
import { TenantVideoModel } from './models/tenants/tenant-video.model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  userRole: string = '';
  tenantType!: TenantTypeModel;
  userLocation: OrganisationUnitModel | null = null;
  selectedRoute = '';
  lastUrl = '/';
  isLearningPathAssigned: boolean = false;
  isComplianceCategoryAssigned: boolean = false;
  menuOpened: boolean = true;
  menuMode: DxDrawerTypes.OpenedStateMode = 'shrink';
  menuRevealMode: DxDrawerTypes.RevealMode = 'slide'; //'slide';
  minMenuSize = 0;
  shaderEnabled = false;
  isIframe = false;
  user: PersonModel | null = null;
  iddResponsiblePerson: PersonModel | null = null;
  logoUrl: string | null = null;
  currentPerson$ = new Subject<AuthenticatedPersonModel>();
  isAnonymousRoute: boolean = false;
  anonymousRoutes: string[] = [
    '/feedback/v2', '/Feedback/v2', '/feedback', '/Feedback', '/my-invitation', '/confirm-participation', 
    '/scorm-player', '/register', '/login', '/trainer-details', '/broker-confirmation', '/events',
    '/broker-register', '/broker-registration-confirmation'
  ];
  lastCheckedBuildTime: number | null = null;
  isLargeScreen: boolean = true;

  showOnboarding: boolean = false;
  
  showRegistrationOnboarding: boolean = false;
  pendingRegistrationFlow: PendingRegistrationFlowModel | null = null;

  showTenantVideo: boolean = false;
  pendingTenantVideo: TenantVideoModel | null = null;


  get supportIcon() {
    return this.sanitized.bypassSecurityTrustHtml(supportIcon);
  }

  get userName() {
    return `${this.user!.firstName} ${this.user!.lastName}`;
  }

  constructor(
    private router: Router,
    private authorizationService: AuthorizationService,
    private sanitized: DomSanitizer,
    private storageService: StorageService,
    private breakpointObserver: BreakpointObserver,
    private appInsights: AppInsightsService,
    private ngxFavicon: AngularFaviconService,
    private personService: PersonService,
    translate: TranslateService,
    private snackBar: MatSnackBar,
    private userContext: UserContextService,
    private segmentTranslationService: SegmentTranslationService,
    private onboardingService: OnboardingService,
    private drawerService: DrawerService,
    private settingsService: GeneralSettingsService,
    private tenantContext: TenantContextService,
    private tenantRedirectService: TenantRedirectService,
    private registrationFlowService: RegistrationFlowService,
    private tenantVideoService: TenantVideoService
  ) {
    config({ licenseKey });

    this.authorizationService.accessToken$
      .pipe(filter(token => !!token))
      .subscribe((token) => {
        fetchSetup.fetchSettings = {
          headers: { 
            'Authorization': 'Bearer ' + token 
          }
        };
      });

    this.authorizationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter(authenticated => authenticated),
        switchMap(() => this.settingsService.getTenant())
      )
      .subscribe(tenant => {
        this.tenantType = tenant.type;
        this.tenantContext.setTenant(tenant);
        
        const lang = translate.currentLang || 'en';
        this.segmentTranslationService.loadSegmentTranslations(lang);

        this.tenantRedirectService.enforceTenantDomain();
      });

    translate.onLangChange
      .pipe(
        map(event => event.lang),
        distinctUntilChanged(),
        filter(() => this.authorizationService.isAuthenticatedSnapshot)
      )
      .subscribe(lang  => {
        this.segmentTranslationService.loadSegmentTranslations(lang);
      });

    this.currentPerson$.subscribe((result) => {
      translate.use(result.person.culture).subscribe(() => {
        forkJoin({
          isLearningPathAssigned:  this.personService.hasAssignedLearningPath(),
          isComplianceCategoryAssigned:  this.personService.hasAssignedComplianceCategory(),
          iddResponsiblePerson: this.personService.getIddResponsiblePerson(),
          isOnboardingCompleted: this.onboardingService.isOnboardingCompleted(),
          pendingRegistrationFlow: this.registrationFlowService.getPendingRegistrationFlow(),
          pendingTenantVideo: this.tenantVideoService.getPendingTenantVideo()
        })
        .subscribe(({
          isLearningPathAssigned, isComplianceCategoryAssigned, 
          iddResponsiblePerson, isOnboardingCompleted,
          pendingRegistrationFlow, pendingTenantVideo
        }) => {
          this.isLearningPathAssigned = isLearningPathAssigned;
          this.isComplianceCategoryAssigned = isComplianceCategoryAssigned;
          this.showOnboarding = !isOnboardingCompleted;

          if (pendingRegistrationFlow) {
            this.pendingRegistrationFlow = pendingRegistrationFlow;
            this.showRegistrationOnboarding = true;
          }

          if (pendingTenantVideo) {
            this.pendingTenantVideo = pendingTenantVideo;
            this.showTenantVideo = true;
          }

          if (result.person.culture == 'en') {
            loadMessages(enMessages);
            locale('en-EN');
          } else if (result.person.culture == 'de') {
            loadMessages(deMessages);
            locale('de-DE');
          } else if (result.person.culture === 'es') {
            loadMessages(esMessages);
            locale('es-ES');
          } else if (result.person.culture === 'fr') {
            loadMessages(frMessages);
            locale('fr-FR');
          } else if (result.person.culture === 'it') {
            loadMessages(itMessages);
            locale('it-IT');
          } else if (result.person.culture === 'nl') {
            loadMessages(nlMessages);
            locale('nl-NL');
          } else if (result.person.culture === 'pl') {
            loadMessages(plMessages);
            locale('pl-PL');
          }
  
          this.iddResponsiblePerson = iddResponsiblePerson;
          
          this.userRole = result.person.role.name;
          this.userLocation = result.person.location;
          this.user = result.person;
          this.logoUrl = result.logoUrl;

          this.userContext.setRole(this.userRole);
          this.userContext.setCurrentPerson(result);
        });
      });
    });

    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this.lastUrl = event.url;
      }

      if (event instanceof NavigationEnd) {
        this.selectedRoute = event.urlAfterRedirects.split('?')[0];
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        map((event) => event as NavigationStart),
        filter((navigationStartEvent) => {
          this.isAnonymousRoute = this.anonymousRoutes.some((anonymousRoute) =>
            navigationStartEvent.url.includes(anonymousRoute)
          );

          if (this.isAnonymousRoute) {
            // @ts-ignore
            const userLanguage = navigator.language || navigator.userLanguage;
            const culture = userLanguage.split('-')[0];

            translate.use(culture).subscribe(() => {
              if (userLanguage === 'de-DE') {
                loadMessages(deMessages);
                locale('de-DE');
              } else if (userLanguage === 'es-ES') {
                loadMessages(esMessages);
                locale('es-ES');
              } else if (userLanguage === 'fr-FR') {
                loadMessages(frMessages);
                locale('fr-FR');
              } else if (userLanguage === 'it-IT') {
                loadMessages(itMessages);
                locale('it-IT');
              } else if (userLanguage === 'nl-NL') {
                loadMessages(nlMessages);
                locale('nl-NL');
              } else if (userLanguage === 'pl-PL') {
                loadMessages(plMessages);
                locale('pl-PL');
              } else {
                loadMessages(enMessages);
                locale('en-EN');
              }
            });
          }

          return !this.isAnonymousRoute;
        }),

        switchMap(() => this.authorizationService.isAuthenticated$),
        tap(isAuthenticated => {
          if (!isAuthenticated) {
            const currentUrl = this.lastUrl || '/';

            if (currentUrl === '/' || currentUrl === AppRoutes.login) {
              this.router.navigate([AppRoutes.login]);
              return;
            }

            this.router.navigate([AppRoutes.login], {
              queryParams: { returnUrl: currentUrl }
            });
          }
        }),
        filter(isAuthenticated => isAuthenticated),//Only continue when already authenticated. When not authenticated previous step redirects user to /login
        switchMap(() => this.authorizationService.getCurrentPerson()),
        shareReplay(1)
      )
      .subscribe((person) => this.currentPerson$.next(person));

    this.breakpointObserver
      .observe('(min-width: 1441px)')
      .subscribe((result) => {
        this.handleScreenWidth(result.breakpoints['(min-width: 1441px)']);
      });
  }

  handleScreenWidth(isLargeScreen: boolean) {
    this.isLargeScreen = isLargeScreen;

    if (isLargeScreen) {
      this.shaderEnabled = false;
      this.menuOpened = true;
    } else {
      this.shaderEnabled = true;
      this.menuOpened = false;
    }
  }

  closeDrawer() {
    if (this.shaderEnabled) {
      this.menuOpened = false;
    }
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;
    this.ngxFavicon.setFavicon('assets/favicon.ico', 'assets/favicon_dark.ico');

    this.drawerService.openDrawerChanged$.subscribe(() => {
      this.menuOpened = true;
    });

    this.drawerService.closeDrawerChanged$.subscribe(() => {
      if (!this.isLargeScreen) {
        this.menuOpened = false;
      }
    });

    this.checkForUpdates();
  }

  navigationChanged(event: DxTreeViewTypes.ItemClickEvent) {
    const path = (event.itemData as any).path;

    if (path == '/training') {
      this.storageService.set(StorageKey.TRAINING_OVERVIEW_TAB_INDEX, '0');
    }
    if (path) {
      this.router.navigate([path]);
      this.closeDrawer();
    }
  }


  private checkForUpdates() {
    setInterval(() => {
      fetch('assets/version.json?' + Date.now())
        .then(response => response.json())
        .then(data => {
          if (this.lastCheckedBuildTime && data.lastBuildTime > this.lastCheckedBuildTime) {
            const snackBar = this.snackBar.open(
              'A new version is available!',
              'Update now',
              {
                duration: 0,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              }
            );
            snackBar.onAction().subscribe(() => {
              window.location.reload();
            });
          }
          this.lastCheckedBuildTime = data.lastBuildTime;
          console.log('Last checked build time:', this.lastCheckedBuildTime);
        });
    }, 10000);
  }

  onRegistrationFlowOnboardingCompleted() {
    this.showRegistrationOnboarding = false;
    this.pendingRegistrationFlow = null; 
  }

  onTenantVideoCompleted() {
    this.showTenantVideo = false;
    this.pendingTenantVideo = null; 
  }
}
